import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { animate, style, transition, trigger } from '@angular/animations';
import { AccessibilityService } from '../../../services/accessibility';

@Component({
  selector: 'app-ada-menu',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDividerModule,
    MatTooltipModule,
  ],
  templateUrl: './ada-menu.html',
  styleUrl: './ada-menu.css',
  animations: [
    trigger('expandUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)', transformOrigin: 'bottom right' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        animate(
          '150ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 0, transform: 'scale(0.95)', transformOrigin: 'bottom right' }),
        ),
      ]),
    ]),
  ],
})
export class AdaMenu implements OnInit {
  /** Whether the accessibility menu panel is currently open. */
  isOpen = false;

  /** Docks the widget to the bottom-right instead of the default bottom-left. */
  @Input() alignRight = false;

  /**
   * Available color blind filter options shown in the dropdown.
   * @readonly
   */
  readonly colorBlindOptions = [
    { value: 'none', label: 'None' },
    { value: 'deuteranopia', label: 'Red-green color blindness' },
    { value: 'tritanopia', label: 'Blue-yellow (Tritanopia)' },
    { value: 'grayscale', label: 'Grayscale' },
  ];

  constructor(
    /** Service for managing and persisting accessibility preferences. */
    public a11y: AccessibilityService,
  ) {}

  /**
   * Loads saved accessibility preferences on init.
   * @returns {void}
   */
  ngOnInit(): void {
    this.a11y.loadPrefs();
  }

  /**
   * Applies updated CSS classes and persists preferences after a toggle change.
   * @param {'dyslexiaFont' | 'largeText' | 'reduceMotion' | 'highContrast'} pref - The preference that was toggled.
   * @returns {void}
   */
  toggle(pref: 'dyslexiaFont' | 'largeText' | 'reduceMotion' | 'highContrast'): void {
    this.a11y.applyClasses();
    this.a11y.savePrefs();
  }

  /**
   * Applies updated CSS classes and persists preferences after a color blind filter change.
   * @returns {void}
   */
  onColorBlindChange(): void {
    this.a11y.applyClasses();
    this.a11y.savePrefs();
  }

  /**
   * Toggles the accessibility menu panel open or closed.
   * @returns {void}
   */
  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  /**
   * Returns true if any accessibility preference is currently active.
   * Delegates to AccessibilityService.anyActive.
   * @returns {boolean}
   */
  get anyActive(): boolean {
    return this.a11y.anyActive;
  }
}
